import { css } from 'aphrodite';
import { useState } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useClearTermsOfServiceAckMutation } from '../../../queries';
import { Checkbox } from '../../components-v2/Checkbox';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '../../components-v2/Dialog';
import { baseStyleSheet } from '../../styles/styles';
import { styleSheet } from './styles';

export function HIPAATermsAcknowledgement({
	pendingActionId,
	onComplete,
}: {
	pendingActionId: string;
	onComplete: () => void;
}) {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('HIPAATermsAcknowledgement');
	const [isAcknowledged, setIsAcknowledged] = useState(false);

	const clearTermsAck = useClearTermsOfServiceAckMutation({
		onError: error => {
			logApiError('ClearTermsOfService-Error', error);
		},
		onSuccess: () => {
			userSession.removePendingAccountAction(pendingActionId);
			onComplete();
		},
	});

	const handleAcknowledge = () => {
		if (!isAcknowledged) {
			return;
		}

		clearTermsAck.mutate();
	};

	return (
		<Dialog defaultOpen={true}>
			<DialogPopup showCloseButton={false} overlay={true} keepMounted={true} styles={[styleSheet.dialogPopup]}>
				<DialogHeader>
					<DialogTitle>HIPAA Terms Acknowledgement</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styleSheet.dialogBody]}>
					<p className={css(styleSheet.text)}>
						By using Levitate, you acknowledge and agree to comply with the Health Insurance Portability and
						Accountability Act (HIPAA) regulations when handling protected health information (PHI).
					</p>
					<p className={css(styleSheet.text)}>
						You must ensure that any PHI shared through this platform is done so in accordance with HIPAA privacy and
						security requirements, including obtaining necessary patient authorizations and implementing appropriate
						safeguards.
					</p>
					<div className={css(styleSheet.ack)}>
						<Checkbox
							id='hipaa-terms-agreement-checkbox'
							size='lg'
							onCheckedChange={value => setIsAcknowledged(value === true)}
							checked={isAcknowledged}
						/>
						<label htmlFor='hipaa-terms-agreement-checkbox' className={css(styleSheet.ackLabel)}>
							I have read and agree to the Levitate{' '}
							<a
								className={css(baseStyleSheet.brandLink, styleSheet.link)}
								href='https://www.levitate.ai/terms-of-service'
								target='_blank'
								rel='noreferrer'
							>
								Terms of Service
							</a>
							{', '}
							<a
								className={css(baseStyleSheet.brandLink, styleSheet.link)}
								href='https://www.levitate.ai/privacy-policy'
								target='_blank'
								rel='noreferrer'
							>
								Privacy Policy
							</a>
							{', and '}
							<a
								className={css(baseStyleSheet.brandLink, styleSheet.link)}
								href='https://www.levitate.ai/baa-acknowledgement'
								target='_blank'
								rel='noreferrer'
							>
								BAA Acknowledgement
							</a>
							{', and I acknowledge my responsibilities under HIPAA when handling protected health information.'}
						</label>
					</div>
				</DialogBody>
				<DialogFooter styles={[styleSheet.dialogFooter]}>
					<button
						className={css(baseStyleSheet.ctaButton, styleSheet.acknowledgeButton)}
						disabled={!isAcknowledged || clearTermsAck.isPending}
						onClick={handleAcknowledge}
					>
						{clearTermsAck.isPending ? 'Acknowledging...' : 'I Acknowledge'}
					</button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
